import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, CheckCircle2, ArrowRight } from "lucide-react";
import { http, trackEvent } from "@/lib/api";
import { Card, PageTitle, btnPrimary, btnGhost, fieldCls, StatusPill } from "@/components/portal/ui";

// Full Blueprint intake. Reuses the Diagnostic (foundational) answers automatically — parents never start over.
const CONCERN_LABELS = { constant_waking: "Frequent night waking", short_naps: "Short naps", crib: "Crib / settling",
  bedtime: "Bedtime resistance", regression: "Sudden change / regression", unsure: "Not sure" };

function Field({ label, children }) { return <div><label className="block text-sm text-[#6B665E] mb-2">{label}</label>{children}</div>; }

export default function BlueprintIntake() {
  const nav = useNavigate();
  const [foundational, setFoundational] = useState(null);
  const [bp, setBp] = useState({});
  const [state, setState] = useState("Purchased");
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef();
  const completedRef = useRef(false);

  useEffect(() => {
    trackEvent("blueprint_intake_started", {}, { once: true });
    http.get("/portal/intake").then((r) => { setFoundational(r.data.answers || {}); setBp(r.data.blueprint_answers || {}); }).catch(() => setFoundational({}));
    http.get("/portal/overview").then((r) => { const s = r.data.services.find((x) => x.product_key === "blueprint"); if (s) setState(s.state); }).catch(() => {});
  }, []);

  const set = (k, v) => {
    setBp((prev) => {
      const next = { ...prev, [k]: v };
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => { try { await http.put("/portal/intake/blueprint", { answers: next }); setSavedAt(new Date()); } catch (e) {} }, 700);
      return next;
    });
  };
  const submit = async () => {
    try { await http.put("/portal/intake/blueprint", { answers: bp }); await http.post("/portal/intake/blueprint/submit"); if (!completedRef.current) { completedRef.current = true; trackEvent("blueprint_intake_completed", {}); } toast.success("Blueprint intake submitted"); nav("/portal"); }
    catch (e) { toast.error("Could not submit"); }
  };

  const submitted = ["Awaiting Operator", "In Preparation", "Ready", "Delivered", "Viewed", "Completed"].includes(state);
  if (foundational === null) return <div className="text-[#6B665E]">Loading…</div>;

  if (submitted) {
    return (
      <div>
        <PageTitle eyebrow="Blueprint" title="Blueprint intake submitted" />
        <Card className="p-8 max-w-2xl text-center">
          <CheckCircle2 className="mx-auto text-[#667A66]" size={44} />
          <p className="font-display text-2xl text-[#1A1918] mt-4">Your Certified Infant Sleep Consultant is on it.</p>
          <p className="text-[#6B665E] mt-2">We have everything needed to build your complete personalized sleep plan. We'll notify your portal when your Blueprint is ready.</p>
          <div className="mt-5"><StatusPill state={state} /></div>
        </Card>
      </div>
    );
  }

  const hasDiag = foundational && Object.keys(foundational).length > 0;

  return (
    <div>
      <PageTitle eyebrow="Blueprint intake · the full planning intake" title="Let's build your complete plan"
        sub="This is the deeper, full structured intake behind your Personalized Sleep Blueprint. It goes further than the Diagnostic questionnaire." />
      <div className="flex items-center gap-2 text-sm text-[#8A998C] mb-5"><Save size={14} /> {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Your progress saves automatically"}</div>

      <div className="max-w-2xl space-y-4">
        {hasDiag && (
          <Card className="p-6">
            <div className="eyebrow text-[#9F634E] mb-3">Review & update your information</div>
            <p className="text-[#6B665E] text-sm mb-4">We've carried everything forward from your Diagnostic — no need to start over. Confirm it still looks right; you only need to answer the additional planning questions below.</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {foundational.age_months && <Row k="Baby's age" v={`${foundational.age_months} months`} />}
              {foundational.primary_concern && <Row k="Primary concern" v={CONCERN_LABELS[foundational.primary_concern] || foundational.primary_concern} />}
              {foundational.bedtime && <Row k="Current bedtime" v={foundational.bedtime} />}
              {foundational.falls_asleep && <Row k="Falls asleep by" v={foundational.falls_asleep} />}
              {foundational.wakings && <Row k="Night wakings" v={foundational.wakings} />}
              {foundational.naps && <Row k="Naps / day" v={foundational.naps} />}
              {foundational.environment && <Row k="Environment" v={foundational.environment} />}
              {foundational.goals && <Row k="Main goal" v={foundational.goals} />}
            </div>
            <button onClick={() => nav("/portal/diagnostic")} className="text-[#9F634E] text-sm mt-4 hover:underline">Update foundational answers →</button>
          </Card>
        )}

        <Card className="p-6 space-y-5">
          <div className="eyebrow text-[#9F634E]">Daily rhythm & schedule</div>
          <Field label="Walk us through a typical full day (wake time, naps, meals, bedtime)"><textarea rows={3} data-testid="bp-day" className={fieldCls} value={bp.typical_day || ""} onChange={(e) => set("typical_day", e.target.value)} /></Field>
          <Field label="Wake windows — how long is your baby comfortably awake between sleeps?"><input className={fieldCls} placeholder="e.g. 2–2.5h" value={bp.wake_windows || ""} onChange={(e) => set("wake_windows", e.target.value)} /></Field>
          <Field label="Current nap schedule & lengths in detail"><textarea rows={2} className={fieldCls} value={bp.nap_detail || ""} onChange={(e) => set("nap_detail", e.target.value)} /></Field>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="eyebrow text-[#9F634E]">Bedtime, settling & nights</div>
          <Field label="Bedtime routine, step by step"><textarea rows={2} className={fieldCls} value={bp.bedtime_routine || ""} onChange={(e) => set("bedtime_routine", e.target.value)} /></Field>
          <Field label="How does your baby fall asleep at the start of the night (onset)?"><textarea rows={2} className={fieldCls} value={bp.sleep_onset || ""} onChange={(e) => set("sleep_onset", e.target.value)} /></Field>
          <Field label="Current settling method (what you do to help them settle)"><textarea rows={2} className={fieldCls} value={bp.settling_method || ""} onChange={(e) => set("settling_method", e.target.value)} /></Field>
          <Field label="What happens during night wakings (who goes in, what you do)?"><textarea rows={2} className={fieldCls} value={bp.night_detail || ""} onChange={(e) => set("night_detail", e.target.value)} /></Field>
          <Field label="Night feeds — how many, and when (if relevant)?"><input className={fieldCls} value={bp.night_feeds || ""} onChange={(e) => set("night_feeds", e.target.value)} /></Field>
          <Field label="Sleep environment (room, lighting, sound, temperature, sleep space)"><textarea rows={2} className={fieldCls} value={bp.environment_detail || ""} onChange={(e) => set("environment_detail", e.target.value)} /></Field>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="eyebrow text-[#9F634E]">Family, constraints & preferences</div>
          <Field label="Family routines & anything that shapes your days/nights"><textarea rows={2} className={fieldCls} value={bp.family_routines || ""} onChange={(e) => set("family_routines", e.target.value)} /></Field>
          <Field label="Childcare / daycare arrangements (if any)"><textarea rows={2} className={fieldCls} value={bp.childcare || ""} onChange={(e) => set("childcare", e.target.value)} /></Field>
          <Field label="Relevant developmental context (teething, milestones, illness, feeding changes)"><textarea rows={2} className={fieldCls} value={bp.developmental || ""} onChange={(e) => set("developmental", e.target.value)} /></Field>
          <Field label="Strategies you've already tried, and how they went"><textarea rows={2} data-testid="bp-tried" className={fieldCls} value={bp.strategies_tried || ""} onChange={(e) => set("strategies_tried", e.target.value)} /></Field>
          <Field label="How would you describe your implementation style / preferences?"><textarea rows={2} className={fieldCls} value={bp.implementation_pref || ""} onChange={(e) => set("implementation_pref", e.target.value)} /></Field>
          <Field label="What are you comfortable changing?"><textarea rows={2} className={fieldCls} value={bp.comfortable_changing || ""} onChange={(e) => set("comfortable_changing", e.target.value)} /></Field>
          <Field label="What would you prefer NOT to change?"><textarea rows={2} className={fieldCls} value={bp.not_changing || ""} onChange={(e) => set("not_changing", e.target.value)} /></Field>
          <Field label="Your goals & any constraints"><textarea rows={2} className={fieldCls} value={bp.goals_constraints || ""} onChange={(e) => set("goals_constraints", e.target.value)} /></Field>
        </Card>

        <div className="flex gap-3">
          <button onClick={async () => { try { await http.put("/portal/intake/blueprint", { answers: bp }); toast.success("Progress saved"); } catch (e) {} }} className={btnGhost}>Save & continue later</button>
          <button data-testid="bp-submit" onClick={submit} className={btnPrimary}>Submit for my Blueprint <ArrowRight size={15} className="inline ml-1" /></button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-[#F0ECE4] py-1.5"><span className="text-[#8A998C]">{k}</span><span className="text-[#1A1918] text-right">{String(v)}</span></div>;
}
